import type { TaskOwnershipFilter } from '@src/features/tasks/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PanelFilter from './PanelFilter';

type TasksOwnershipFilterProps = {
  value: TaskOwnershipFilter;
  onChange: (value: TaskOwnershipFilter) => void;
};

/**
 * Splits a section's panel into the questions put to the viewer and the ones
 * they asked. Both are theirs to keep an eye on, but only one is theirs to
 * answer, and a list mixing them says nothing about which is which.
 */
export default function TasksOwnershipFilter({
  value,
  onChange,
}: TasksOwnershipFilterProps) {
  const { t } = useTranslation();

  // "All" leads because it is what the panel opens on.
  const options = useMemo(
    () => [
      { label: t('common.all', 'All'), value: 'all' as const },
      {
        label: t('tasks.filter_assigned_to_me', 'Assigned to me'),
        value: 'assigned' as const,
      },
      {
        label: t('tasks.filter_raised_by_me', 'Raised by me'),
        value: 'raised' as const,
      },
    ],
    [t],
  );

  return (
    <PanelFilter
      label={t('tasks.filter_ownership_label', 'Task ownership filter')}
      value={value}
      options={options}
      fallback="all"
      onChange={onChange}
    />
  );
}
